import { createJustText } from '@vfde-brix/ws10/just-text';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { mountComponent } from '../util/mount';
import {
    CustomerInfoId,
    DiscountId,
    HtmlId,
    LegalId,
} from '../enums/elementId';
import {
    convertToPriceString,
    createTvPickerText,
    createYoungPickerText,
} from '../util/formatter';
import {
    CustomerInfo,
    FootnoteHtmlContent,
    JarvisProduct,
    JarvisTvProduct,
} from '../interface';
import { Address } from '@vfde-sails/avc-core';
import { getUrl } from '../util/url';
import { QueryNames } from '../enum';

/**
 * Create Brix Just Text component.
 *
 * @param id - Wrapper ID of the element.
 * @returns Just Text component
 * or null if the element with the given ID is not found.
 */
export const mountJustText = (id: HtmlId) =>
    mountComponent(id, createJustText, NO_PATTERN_BUSINESS_LOGIC);

/**
 * Updates the discounted price text in the legal overlay
 */
export const updateLegalDiscountedPrice = (price: number) => {
    const legalPriceEl = document.getElementById(LegalId.DISCOUNTED_PRICE);

    if (!legalPriceEl) {
        return;
    }

    const priceText = convertToPriceString(price);

    legalPriceEl.innerText = priceText;
};

/**
 * Updates the discounted price above the tv picker
 */
export const updateTvPickerText = ({ discountedPrice, name, id }: JarvisTvProduct) => {
    const pickerTextEl = document.getElementById(DiscountId.TV_CONNECT_DISCOUNTED_PRICE_TEXT);

    if (!pickerTextEl) {
        return;
    }

    pickerTextEl.innerHTML = createTvPickerText(discountedPrice.value, name);
    pickerTextEl.parentElement?.setAttribute('data-metacode', id);
};

/**
 * Updates the price(s) above the young picker
 */
export const updateYoungPickerText = ({ youngDiscount, youngPromoDiscount }: JarvisProduct) => {
    const pickerTextEl = document.getElementById(DiscountId.YOUNG_PICKER_TEXT);

    if (!pickerTextEl || (!youngDiscount || !youngDiscount.price)) {
        return;
    }

    pickerTextEl.innerHTML = createYoungPickerText(youngDiscount.price.value, youngPromoDiscount?.price.value);
};

/**
 * Updates the footnote texts in the legal overlay
 */
export const updateLegalFootnoteText = (footnoteContent: FootnoteHtmlContent) => {
    const titleEl = document.getElementById(LegalId.FOOTNOTE_HEADLINE);
    const textEl = document.getElementById(LegalId.FOOTNOTE_TEXT);

    if (!titleEl || !textEl) {
        return;
    }

    titleEl.innerHTML = footnoteContent.contentTitle;
    textEl.innerHTML = footnoteContent.content;
};

/**
 * Updates login redirect link on the legal overlay
 */
export const updateLegalLoginLink = () => {
    const loginLinkElement = document.getElementById(LegalId.LOGIN_LINK) as HTMLAnchorElement;
    const loginLink = loginLinkElement.href;

    if (!loginLinkElement || !loginLink) {
        return;
    }

    loginLinkElement.href = getUrl(loginLink, {
        [QueryNames.TARGET_URL]: window.location.href,
    });
};

/**
 * Updates customer info module with user info
 */
export const updateCustomerInfo = (customer: CustomerInfo, address: Address) => {
    const nameEl = document.getElementById(CustomerInfoId.NAME);
    const cityEl = document.getElementById(CustomerInfoId.CITY);
    const streetAndNumberEl = document.getElementById(CustomerInfoId.STREET_AND_HOUSE);
    const customerNumberEl = document.getElementById(CustomerInfoId.CUSTOMER_NUMBER);

    if (!nameEl || !cityEl || !streetAndNumberEl || !customerNumberEl) {
        return;
    }

    nameEl.innerText = `${customer.firstName} ${customer.lastName}`;
    cityEl.innerText = `${address.zipcode || ''} ${address.city || ''}`;
    streetAndNumberEl.innerText = `${address.street || ''} ${address.housenumber || ''}${address.housenumberextra || ''}`;
    customerNumberEl.innerText = customer.accountId;
};

/**
 * updateGigakombiPrice
 * Updates Gigakombi bonus price above picker
 *
 * @param price - Price to be updated
 */
export const updateGigakombiPrice = (price: number) => {
    const gigakombiPriceElement = document.getElementById(DiscountId.GIGAKOMBI_BONUS_PRICE);

    if (gigakombiPriceElement) {
        gigakombiPriceElement.innerText = convertToPriceString(Math.abs(price));
    }
};
